<template>
  <div class="warehouse-stock-check-page">
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">库位盘点</h1>
      <el-button type="primary" size="small" @click="showAddDialog = true"
        >新增</el-button
      >
    </header>

    <main class="content">
      <div class="filter-bar">
        <el-button
          v-for="opt in statusOptions"
          :key="opt.value"
          :type="checkStatusFilter === opt.value ? 'primary' : ''"
          size="small"
          @click="
            checkStatusFilter = opt.value;
            fetchList(1);
          "
          >{{ opt.label }}</el-button
        >
      </div>

      <el-input
        v-model="searchText"
        placeholder="搜索库位名称"
        clearable
        @clear="fetchList(1)"
        class="search-bar"
      />

      <el-alert
        v-if="statusMsg"
        :title="statusMsg"
        :type="
          statusType === 'success'
            ? 'success'
            : statusType === 'error'
            ? 'error'
            : 'info'
        "
        show-icon
        :closable="false"
        class="status-alert"
      />

      <div v-if="loading" v-loading="true" class="loading-area" />

      <el-empty v-if="!loading && list.length === 0" description="暂无盘点记录">
        <el-button type="primary" @click="showAddDialog = true"
          >立即创建</el-button
        >
      </el-empty>

      <div v-else-if="!loading" class="card-list">
        <div
          v-for="item in list"
          :key="item.id"
          class="stock-card"
          @click="navigateToDetail(item.id)"
        >
          <div class="card-top">
            <span class="card-no">{{ item.inventeNo }}</span>
            <el-tag
              :type="getTagType(item.checkStatus)"
              size="small"
              effect="dark"
              round
              >{{ getStatusText(item.checkStatus) }}</el-tag
            >
          </div>
          <div class="card-info">
            <div class="info-row">
              <span class="info-label">创建时间</span>
              <span class="info-value">{{ formatDate(item.createTime) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">库位</span>
              <span class="info-value">{{ item.wareName || "-" }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">供应商</span>
              <span class="info-value">{{ item.supplierName || "-" }}</span>
            </div>
            <div v-if="item.checkStatus === 2" class="info-row">
              <span class="info-label">不通过原因</span>
              <span class="info-value text-danger">{{ item.reason }}</span>
            </div>
          </div>
          <div class="card-actions" @click.stop>
            <el-button
              type="primary"
              size="small"
              @click="navigateToDetail(item.id)"
            >
              {{
                item.checkStatus === 0
                  ? "开始盘点"
                  : item.checkStatus === 2
                  ? "审核未通过"
                  : "审核通过"
              }}
            </el-button>
            <el-button
              size="small"
              type="danger"
              plain
              :loading="deletingId === item.id"
              @click="handleDelete(item.id)"
            >
              {{ deletingId === item.id ? "" : "删除" }}
            </el-button>
          </div>
        </div>
      </div>

      <div v-if="total > 0 && !loading" class="pagination-wrap">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="total"
          :page-size="size"
          :current-page="current"
          @current-change="handlePageChange"
          size="small"
        />
      </div>
    </main>

    <el-dialog
      v-model="showAddDialog"
      title="新增库位盘点单"
      width="95%"
      :close-on-click-modal="false"
      top="10vh"
      @closed="resetDialogFields"
    >
      <el-alert
        v-if="dialogMsg"
        :title="dialogMsg"
        :type="dialogMsgType"
        show-icon
        :closable="false"
        class="status-alert"
      />

      <el-form-item label="新增方式" class="form-item">
        <el-radio-group v-model="addMethod" @change="handleAddMethodChange">
          <el-radio-button value="pallet">按托盘</el-radio-button>
          <el-radio-button value="supplier">按供应商</el-radio-button>
        </el-radio-group>
      </el-form-item>

      <template v-if="addMethod === 'pallet'">
        <el-form-item label="扫码/输入托盘码" class="form-item">
          <div class="select-with-scan">
            <el-select
              v-model="selectedPalletCode"
              filterable
              remote
              reserve-keyword
              placeholder="请扫描或输入托盘码"
              :remote-method="handlePalletSearch"
              :loading="palletLoading"
              @change="handlePalletChange"
              style="width: 100%"
            >
              <el-option
                v-for="p in palletOptions"
                :key="p.id"
                :label="p.palletCode"
                :value="p.palletCode"
              >
                <div>{{ p.palletCode || "-" }}</div>
                <div style="font-size: 11px; color: #999">
                  {{ p.storeName || "" }}
                  {{ p.wareAreaName ? `| ${p.wareAreaName}` : "" }}
                  {{ p.wareName ? `| ${p.wareName}` : "" }}
                </div>
              </el-option>
            </el-select>
            <el-button
              type="text"
              class="scan-btn"
              @click="startScan"
              aria-label="扫码"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                width="20"
                height="20"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <path d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z" />
              </svg>
            </el-button>
          </div>
        </el-form-item>

        <div
          v-if="palletDetailLoading"
          v-loading="true"
          style="min-height: 60px"
        />

        <div
          v-else-if="palletDetailGroups.length > 0"
          class="pallet-detail-section"
        >
          <div
            v-for="(group, gIdx) in palletDetailGroups"
            :key="gIdx"
            class="supplier-group"
          >
            <div class="supplier-group-title">{{ group.supplierName }}</div>
            <el-table
              :data="group.materials"
              stripe
              size="small"
              class="material-table"
            >
              <el-table-column
                prop="materialNo"
                label="物料编码"
                min-width="120"
              />
              <el-table-column
                prop="materialDesc"
                label="物料描述"
                min-width="100"
                show-overflow-tooltip
              />
              <el-table-column label="良品数量" width="90" align="center">
                <template #default="{ row }">
                  {{ row.goodStockNum.toFixed(2) }}
                </template>
              </el-table-column>
              <el-table-column label="不良品数量" width="100" align="center">
                <template #default="{ row }">
                  {{ row.badStockNum.toFixed(2) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </template>

      <template v-else>
        <el-form-item label="选择项目组" class="form-item">
          <el-select
            v-model="selectedBizDept"
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="输入项目组名称搜索"
            :remote-method="handleBizDeptSearch"
            :loading="bizDeptLoading"
            style="width: 100%"
            @change="handleBizDeptChange"
          >
            <el-option
              v-for="group in bizDeptOptions"
              :key="group.id"
              :label="group.name"
              :value="String(group.id)"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="选择仓库" class="form-item store-form-item">
          <el-radio-group
            v-if="stores.length > 0"
            v-model="selectedStoreId"
            class="store-radio-group"
            @change="handleStoreChange"
          >
            <el-radio
              v-for="store in stores"
              :key="store.id"
              :value="String(store.id)"
              border
              class="store-radio"
            >
              {{ store.wareName }}
            </el-radio>
          </el-radio-group>
          <div v-else-if="storeLoading" class="store-tip">加载中...</div>
          <div v-else class="store-tip">
            {{ selectedBizDept ? "暂无仓库" : "请先选择项目组" }}
          </div>
        </el-form-item>

        <el-form-item label="选择供应商" class="form-item">
          <el-select
            v-model="selectedSupplierId"
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="请先选择仓库，再搜索供应商"
            :disabled="!selectedStoreId"
            :remote-method="handleSupplierSearch"
            :loading="supplierLoading"
            style="width: 100%"
          >
            <el-option
              v-for="item in suppliers"
              :key="item.id"
              :label="item.supplierName"
              :value="String(item.id)"
            >
              <span>{{ item.supplierName }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">{{
                item.supplierCode
              }}</span>
            </el-option>
          </el-select>
        </el-form-item>
      </template>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="resetDialog" style="flex: 1">取消</el-button>
          <el-button
            type="primary"
            :loading="createLoading"
            :disabled="!canCreate"
            @click="onClickCreate"
            style="flex: 1"
          >
            {{ createLoading ? "创建中..." : "创建盘点单" }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <el-dialog
      v-model="showConfirmDialog"
      title="确认创建"
      width="80%"
      :close-on-click-modal="false"
    >
      <p>
        确认为托盘 <strong>{{ selectedPalletCode }}</strong> 创建盘点单吗？
      </p>
      <template #footer>
        <el-button @click="showConfirmDialog = false">取消</el-button>
        <el-button type="primary" :loading="createLoading" @click="handleCreate"
          >确认</el-button
        >
      </template>
    </el-dialog>

    <!-- 扫码组件 -->
    <CameraScan
      v-model:visible="scanVisible"
      title="扫描托盘码"
      @success="handleScanSuccess"
      @close="scanVisible = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import {
  getWareCheckPage,
  warehouseCheckByPallet,
  deleteStockCheck,
  supplierSaveStockCheck,
  getWareList,
} from "@/api/stockCheck";
import {
  getProjectTeams,
  getSuppliersByGroupId,
} from "@/api/projectTeamStrockCheck";
import { getPalletCodeList, getPalletDetail } from "@/api/stockTransfer";
import CameraScan from "@/components/CameraScan";

export default {
  name: "WarehouseStockCheck",
  components: {
    CameraScan,
  },
  setup() {
    const router = useRouter();

    const list = ref([]);
    const loading = ref(false);
    const current = ref(1);
    const size = ref(10);
    const total = ref(0);
    const checkStatusFilter = ref(0);
    const searchText = ref("");
    const statusMsg = ref("");
    const statusType = ref("success");
    const deletingId = ref("");

    const showAddDialog = ref(false);
    const addMethod = ref("pallet");
    const selectedPalletCode = ref("");
    const palletOptions = ref([]);
    const palletLoading = ref(false);
    const scanVisible = ref(false);
    const createLoading = ref(false);
    const dialogMsg = ref("");
    const dialogMsgType = ref("info");
    const palletSearchSeq = ref(0);
    const palletDetailLoading = ref(false);
    const palletDetailGroups = ref([]);
    const showConfirmDialog = ref(false);

    const bizDeptOptions = ref([]);
    const bizDeptLoading = ref(false);
    const selectedBizDept = ref("");
    let bizDeptSearchTimer = null;
    const stores = ref([]);
    const storeLoading = ref(false);
    const selectedStoreId = ref("");
    const suppliers = ref([]);
    const supplierLoading = ref(false);
    const selectedSupplierId = ref("");
    let supplierSearchTimer = null;

    const canCreate = computed(() => {
      if (addMethod.value === "supplier") {
        return (
          !!selectedBizDept.value &&
          !!selectedStoreId.value &&
          !!selectedSupplierId.value
        );
      }
      return !!selectedPalletCode.value;
    });

    const statusOptions = [
      { label: "待盘点", value: 0 },
      { label: "通过", value: 1 },
      { label: "不通过", value: 2 },
    ];

    const fetchList = async (page = 1) => {
      try {
        loading.value = true;
        current.value = page;
        const res = await getWareCheckPage({
          current: page,
          size: size.value,
          checkStatus: checkStatusFilter.value,
          wareName: searchText.value.trim(),
          inventeType: 0,
        });
        if (res.success) {
          list.value = res.data.records || [];
          total.value = res.data.total || 0;
        } else {
          list.value = [];
          total.value = 0;
        }
      } catch (err) {
        console.error("获取盘点列表失败", err);
        list.value = [];
      } finally {
        loading.value = false;
      }
    };

    const startScan = () => {
      scanVisible.value = true;
    };

    const handleScanSuccess = async (decodedText) => {
      const text = String(decodedText || "")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .replace(/[\r\n\t]/g, "")
        .trim();

      if (!text) return;

      scanVisible.value = false;
      selectedPalletCode.value = text;
      await handlePalletSearch(text);
    };

    const handlePalletSearch = async (query) => {
      const keyword = (query || "").trim();
      const seq = palletSearchSeq.value + 1;
      palletSearchSeq.value = seq;

      if (!keyword) {
        palletOptions.value = [];
        return;
      }

      palletLoading.value = true;
      try {
        const res = await getPalletCodeList({ palletCode: keyword });
        if (seq !== palletSearchSeq.value) return;
        const data = res?.data;
        palletOptions.value = Array.isArray(data) ? data : [];
      } catch (e) {
        if (seq !== palletSearchSeq.value) return;
        console.error("搜索托盘码失败", e);
        palletOptions.value = [];
      } finally {
        if (seq === palletSearchSeq.value) {
          palletLoading.value = false;
        }
      }
    };

    const handlePalletChange = async (val) => {
      if (val) {
        const found = palletOptions.value.find((p) => p.palletCode === val);
        if (found) {
          palletOptions.value = [found];
        }
        await fetchPalletDetail(val);
      } else {
        palletDetailGroups.value = [];
      }
    };

    const fetchPalletDetail = async (palletCode) => {
      palletDetailLoading.value = true;
      palletDetailGroups.value = [];
      try {
        const res = await getPalletDetail({
          palletCode,
          current: 1,
          size: 100,
        });
        if (res.success && res.data) {
          const records = res.data.records || [];
          const allMaterials = [];
          records.forEach((record) => {
            if (Array.isArray(record.materialList)) {
              allMaterials.push(...record.materialList);
            }
          });

          const supplierMap = {};
          allMaterials.forEach((m) => {
            const supplierKey = m.supplierId || m.supplierName || "unknown";
            if (!supplierMap[supplierKey]) {
              supplierMap[supplierKey] = {
                supplierName: m.supplierName || "未知供应商",
                materials: {},
              };
            }
            const matKey = m.materialNo || m.id;
            if (!supplierMap[supplierKey].materials[matKey]) {
              supplierMap[supplierKey].materials[matKey] = {
                materialNo: m.materialNo,
                materialDesc: m.materialDesc || "",
                goodStockNum: 0,
                badStockNum: 0,
              };
            }
            const batches = Array.isArray(m.stockList) ? m.stockList : [m];
            batches.forEach((batch) => {
              const qty = parseFloat(batch.stockNum) || 0;
              if (batch.isBad === 1) {
                supplierMap[supplierKey].materials[matKey].badStockNum += qty;
              } else {
                supplierMap[supplierKey].materials[matKey].goodStockNum += qty;
              }
            });
          });

          const groups = Object.values(supplierMap).map((group) => ({
            supplierName: group.supplierName,
            materials: Object.values(group.materials),
          }));
          palletDetailGroups.value = groups;
        }
      } catch (e) {
        console.error("获取托盘详情失败", e);
      } finally {
        palletDetailLoading.value = false;
      }
    };

    const handleAddMethodChange = () => {
      dialogMsg.value = "";
      if (addMethod.value === "supplier" && bizDeptOptions.value.length === 0) {
        fetchBizDepts("");
      }
    };

    const fetchBizDepts = async (keyword = "") => {
      bizDeptLoading.value = true;
      try {
        const res = await getProjectTeams({
          keyword: String(keyword || "").trim(),
          current: 1,
          size: 50,
        });
        const data = res?.data;
        const records = Array.isArray(data) ? data : data?.records || [];
        bizDeptOptions.value = records.map((item) => ({
          id: item.deptId ?? item.dept_id ?? item.id,
          name: item.deptName || item.name,
        }));
      } catch (e) {
        console.error("获取项目组失败", e);
        bizDeptOptions.value = [];
        dialogMsg.value = "获取项目组失败";
        dialogMsgType.value = "error";
      } finally {
        bizDeptLoading.value = false;
      }
    };

    const handleBizDeptSearch = (query) => {
      if (bizDeptSearchTimer) clearTimeout(bizDeptSearchTimer);
      bizDeptSearchTimer = setTimeout(() => {
        fetchBizDepts(query);
      }, 300);
    };

    const handleBizDeptChange = async () => {
      selectedStoreId.value = "";
      stores.value = [];
      selectedSupplierId.value = "";
      suppliers.value = [];
      dialogMsg.value = "";
      if (selectedBizDept.value) {
        await fetchStores(selectedBizDept.value);
      }
    };

    const fetchStores = async (bizDept) => {
      if (!bizDept) {
        stores.value = [];
        return;
      }
      storeLoading.value = true;
      try {
        const res = await getWareList({ bizDept });
        stores.value =
          res && res.success
            ? Array.isArray(res.data)
              ? res.data
              : []
            : [];
      } catch (e) {
        console.error("获取仓库失败", e);
        stores.value = [];
        dialogMsg.value = "获取仓库失败";
        dialogMsgType.value = "error";
      } finally {
        storeLoading.value = false;
      }
    };

    const handleStoreChange = () => {
      selectedSupplierId.value = "";
      suppliers.value = [];
      dialogMsg.value = "";
      if (selectedStoreId.value && selectedBizDept.value) {
        fetchSuppliers("");
      }
    };

    const fetchSuppliers = async (keyword = "") => {
      if (!selectedBizDept.value) {
        suppliers.value = [];
        return;
      }
      supplierLoading.value = true;
      try {
        const res = await getSuppliersByGroupId({
          current: 1,
          size: 50,
          bizDept: selectedBizDept.value,
          supplierName: String(keyword || "").trim(),
        });
        suppliers.value = res?.data?.records || [];
      } catch (e) {
        console.error("获取供应商失败", e);
        suppliers.value = [];
        dialogMsg.value = "获取供应商失败";
        dialogMsgType.value = "error";
      } finally {
        supplierLoading.value = false;
      }
    };

    const handleSupplierSearch = (query) => {
      if (!selectedStoreId.value || !selectedBizDept.value) return;
      if (supplierSearchTimer) clearTimeout(supplierSearchTimer);
      supplierSearchTimer = setTimeout(() => {
        fetchSuppliers(query);
      }, 300);
    };

    const onClickCreate = () => {
      if (addMethod.value === "supplier") {
        handleCreateBySupplier();
        return;
      }
      showConfirmDialog.value = true;
    };

    const handleCreateBySupplier = async () => {
      if (!selectedBizDept.value) {
        dialogMsg.value = "请选择项目组";
        dialogMsgType.value = "warning";
        return;
      }
      if (!selectedStoreId.value) {
        dialogMsg.value = "请选择仓库";
        dialogMsgType.value = "warning";
        return;
      }
      if (!selectedSupplierId.value) {
        dialogMsg.value = "请选择供应商";
        dialogMsgType.value = "warning";
        return;
      }
      try {
        createLoading.value = true;
        dialogMsg.value = "";
        const res = await supplierSaveStockCheck({
          bizDept: String(selectedBizDept.value),
          storeId: String(selectedStoreId.value),
          supplierIds: String(selectedSupplierId.value),
        });
        if (res.success) {
          ElMessage.success("创建盘点单成功");
          resetDialog();
          fetchList(1);
        } else {
          dialogMsg.value = res.msg || "创建失败";
          dialogMsgType.value = "error";
        }
      } catch (err) {
        console.error("创建盘点单失败", err);
        dialogMsg.value = err?.message || "创建盘点单失败，请稍后重试";
        dialogMsgType.value = "error";
      } finally {
        createLoading.value = false;
      }
    };

    const handleCreate = async () => {
      if (!selectedPalletCode.value) {
        dialogMsg.value = "请输入托盘码";
        dialogMsgType.value = "warning";
        return;
      }

      try {
        createLoading.value = true;
        dialogMsg.value = "";
        const res = await warehouseCheckByPallet(selectedPalletCode.value);
        if (res.success) {
          ElMessage.success("创建盘点单成功");
          showConfirmDialog.value = false;
          resetDialog();
          fetchList(1);
        } else {
          dialogMsg.value = res.msg || "创建失败";
          dialogMsgType.value = "error";
        }
      } catch (err) {
        console.error("创建盘点单失败", err);
        dialogMsg.value = "创建盘点单失败，请稍后重试";
        dialogMsgType.value = "error";
      } finally {
        createLoading.value = false;
      }
    };

    const resetDialogFields = () => {
      addMethod.value = "pallet";
      selectedPalletCode.value = "";
      palletOptions.value = [];
      palletDetailGroups.value = [];
      selectedBizDept.value = "";
      bizDeptOptions.value = [];
      selectedStoreId.value = "";
      stores.value = [];
      selectedSupplierId.value = "";
      suppliers.value = [];
      dialogMsg.value = "";
      showConfirmDialog.value = false;
    };

    const resetDialog = () => {
      showAddDialog.value = false;
      resetDialogFields();
    };

    const handleDelete = async (id) => {
      if (!id || deletingId.value) return;
      if (!confirm("确认删除该盘点单吗？")) return;
      try {
        deletingId.value = String(id);
        const res = await deleteStockCheck(id);
        if (res && res.success) {
          ElMessage.success("删除成功");
          fetchList(current.value);
        } else {
          ElMessage.error(res?.msg || "删除失败");
        }
      } catch (err) {
        console.error("删除盘点单失败", err);
        ElMessage.error("删除失败，请稍后重试");
      } finally {
        deletingId.value = "";
      }
    };

    const navigateToDetail = (id) => {
      router.push(`/mobile/location-stock-check/detail/${id}`);
    };

    const handlePageChange = (page) => {
      if (page < 1 || loading.value) return;
      fetchList(page);
    };

    const handleBack = () => router.push("/home");

    const getTagType = (status) => {
      const map = { 0: "info", 1: "success", 2: "danger", 3: "warning" };
      return map[status] || "info";
    };

    const getStatusText = (status) => {
      const map = { 0: "待盘点", 1: "通过", 2: "不通过", 3: "已取消" };
      return map[status] || "未知状态";
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    watch(searchText, () => {
      fetchList(1);
    });

    onMounted(() => fetchList(1));

    return {
      list,
      loading,
      current,
      size,
      total,
      checkStatusFilter,
      searchText,
      statusMsg,
      statusType,
      deletingId,
      statusOptions,
      showAddDialog,
      addMethod,
      canCreate,
      selectedPalletCode,
      palletOptions,
      palletLoading,
      scanVisible,
      startScan,
      handleScanSuccess,
      palletDetailLoading,
      palletDetailGroups,
      showConfirmDialog,
      createLoading,
      dialogMsg,
      dialogMsgType,
      bizDeptOptions,
      bizDeptLoading,
      selectedBizDept,
      handleBizDeptSearch,
      handleBizDeptChange,
      stores,
      storeLoading,
      selectedStoreId,
      handleStoreChange,
      suppliers,
      supplierLoading,
      selectedSupplierId,
      handleAddMethodChange,
      handleSupplierSearch,
      onClickCreate,
      fetchList,
      handlePalletSearch,
      handlePalletChange,
      handleCreate,
      resetDialog,
      resetDialogFields,
      handleDelete,
      navigateToDetail,
      handlePageChange,
      handleBack,
      getTagType,
      getStatusText,
      formatDate,
    };
  },
};
</script>

<style scoped>
.warehouse-stock-check-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: var(--background-color, #f5f5f5);
  /* 与头部青绿主题统一，覆盖 Element 默认蓝 */
  --el-color-primary: var(--primary-color, #23b1c0);
  --el-color-primary-light-3: #4fc3cf;
  --el-color-primary-light-5: #7fd4dc;
  --el-color-primary-light-7: #afe4ea;
  --el-color-primary-light-8: #c7ecf0;
  --el-color-primary-light-9: #dff4f6;
  --el-color-primary-dark-2: #1c8e9a;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--header-background, #23b1c0);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 50px;
  box-sizing: border-box;
}

.header .back-btn {
  background: none;
  border: none;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.header .icon-back {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.header .title {
  font-size: 17px;
  font-weight: 600;
  margin: 0;
  flex: 1;
  text-align: center;
  color: var(--header-text, #fff);
}

.header :deep(.el-button--primary) {
  --el-button-bg-color: rgba(255, 255, 255, 0.2);
  --el-button-border-color: rgba(255, 255, 255, 0.65);
  --el-button-text-color: #fff;
  --el-button-hover-bg-color: rgba(255, 255, 255, 0.3);
  --el-button-hover-border-color: #fff;
  --el-button-hover-text-color: #fff;
  --el-button-active-bg-color: rgba(255, 255, 255, 0.35);
  --el-button-active-border-color: #fff;
}

.content {
  flex: 1;
  padding: 8px 10px;
  padding-top: 58px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.filter-bar > .el-button + .el-button {
  margin-left: 6px;
}

.filter-bar .el-button {
  flex: 1;
}

.search-bar {
  margin-bottom: 8px;
}

.status-alert {
  margin-bottom: 8px;
}

.loading-area {
  min-height: 120px;
}

.card-list {
  display: flex;
  flex-direction: column;
}

.card-list > .stock-card + .stock-card {
  margin-top: 8px;
}

.stock-card {
  background: var(--card-background, #fff);
  border-radius: 8px;
  padding: 10px 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  border-left: 3px solid var(--el-color-primary);
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.card-no {
  font-weight: 600;
  font-size: 14px;
  color: var(--text-primary, #333);
}

.card-info {
  display: flex;
  flex-direction: column;
}

.card-info > .info-row + .info-row {
  margin-top: 3px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  line-height: 1.5;
}

.info-label {
  color: var(--text-secondary, #999);
  flex-shrink: 0;
}

.info-value {
  color: var(--text-primary, #333);
  text-align: right;
  margin-left: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-danger {
  color: var(--el-color-danger);
}

.card-actions {
  display: flex;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--el-border-color-lighter, #ebeef5);
}

.card-actions > .el-button + .el-button {
  margin-left: 8px;
}

.card-actions .el-button {
  flex: 1;
}

.card-actions :deep(.el-button--danger.is-plain) {
  --el-button-bg-color: #fff;
  --el-button-border-color: #dcdfe6;
  --el-button-text-color: #909399;
  --el-button-hover-bg-color: #fef0f0;
  --el-button-hover-border-color: #f56c6c;
  --el-button-hover-text-color: #f56c6c;
  --el-button-active-bg-color: #fef0f0;
  --el-button-active-border-color: #f56c6c;
  --el-button-active-text-color: #f56c6c;
}

.pagination-wrap {
  display: flex;
  justify-content: center;
  padding: 10px 0 20px;
}

.pagination-wrap :deep(.el-pagination.is-background .el-pager li.is-active) {
  background-color: var(--primary-color, #23b1c0);
}

.form-item {
  margin-bottom: 12px;
}

.form-hint {
  margin: -4px 0 12px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.store-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.store-radio {
  margin-right: 0 !important;
  margin-left: 0 !important;
}

.store-form-item :deep(.el-form-item__content) {
  min-height: auto;
  line-height: 1.4;
}

.store-tip {
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 32px;
}

.form-item :deep(.el-form-item__label) {
  font-size: 14px;
  font-weight: 500;
}

.select-with-scan {
  position: relative;
}

.select-with-scan :deep(.el-select__wrapper) {
  padding-right: 40px;
}

.select-with-scan .scan-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  color: var(--primary-color);
}

.pallet-detail-section {
  margin-top: 12px;
  max-height: 50vh;
  overflow-y: auto;
}

.supplier-group {
  margin-bottom: 12px;
}

.supplier-group-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #333);
  padding: 6px 0;
  border-bottom: 2px solid var(--el-color-primary);
  margin-bottom: 4px;
}

.material-table {
  width: 100%;
}

.dialog-footer {
  display: flex;
  gap: 10px;
}

@media (max-width: 768px) {
  .header {
    padding: 8px 10px;
    height: 46px;
  }
  .header .title {
    font-size: 16px;
  }
  .content {
    padding: 6px 8px;
    padding-top: 54px;
  }
  .stock-card {
    padding: 8px 10px;
  }
  .card-no {
    font-size: 13px;
  }
  .info-row {
    font-size: 12px;
  }
}
</style>
